import { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { Card, CircularProgress, Grid, Icon } from "@mui/material"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "examples/Navbars/DashboardNavbar"
import PackageItem from "layouts/calculate-revenue/PackageItem"
import translate from "utils/translate"

// Service
import * as packageService from "services/packages/package.service"
import MDSnackbar from "components/MDSnackbar"
import { diffPrice } from "utils/functions/global"

function CalculateRevenue() {
  const [totalRevenue, setTotalRevenue] = useState(0)
  const [foreignTotalRevenue, setForeignTotalRevenue] = useState(0)
  const [packageList, setPackageList] = useState([])
  const [snackbarObject, setSnackbarObject] = useState({
    open: false,
    title: "",
    content: "",
    color: "primary",
    icon: "",
  })
  const [isLoading, setIsLoading] = useState(false)
  const { control, handleSubmit } = useForm()

  // Handle open snackbar
  const handleOpenSnackbar = (status, title, content, icon) => {
    setSnackbarObject({
      ...snackbarObject,
      title,
      open: true,
      content,
      color: status,
      icon,
    })
  }

  // Handle close snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbarObject({
      ...snackbarObject,
      open: false,
    })
  }

  // Calculate total price for all packages
  const calculateTotalRevenue = async ({ barcodes }) => {
    setIsLoading(true)
    try {
      const splitBarcodes = barcodes.split(/\r?\n/)
      const payload = {
        barcodes: splitBarcodes,
      }
      const {
        data: { total, packages, foreignTotal },
      } = await packageService.calculateTotalRevenue(payload)
      setTotalRevenue(total)
      setForeignTotalRevenue(foreignTotal)
      setPackageList(packages)
      handleOpenSnackbar(
        "success",
        "Comptabilité",
        "Le revenu total du colis a été calculé avec succès",
        "check_circle"
      )
      setIsLoading(false)
    } catch (error) {
      handleOpenSnackbar("error", "Comptabilité", translate.something_wrong, "error")
      setIsLoading(false)
    }
  }

  return (
    <>
      <DashboardLayout>
        <DashboardNavbar absolute />
        <MDBox mt={8}>
          <MDBox mb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Card id="delete-account">
                  <MDBox pt={3} px={2}>
                    <MDTypography variant="h6" fontWeight="medium">
                      Liste du colis
                    </MDTypography>
                  </MDBox>
                  <MDBox pt={1} pb={2} px={2}>
                    <MDBox component="ul" display="flex" flexDirection="column" p={0} m={0}>
                      {packageList.map((packageItem) => (
                        <PackageItem
                          key={packageItem.barcode}
                          name={packageItem.clientName}
                          company={packageItem.companyName}
                          price={packageItem.packagePrice}
                          foreignPriceRatio={packageItem.foreignPrice}
                          barcode={packageItem.barcode}
                        />
                      ))}
                    </MDBox>
                  </MDBox>
                  {packageList.length === 0 && (
                    <MDBox py={8} px={2} display="flex" justifyContent="center" alignItems="center">
                      <MDTypography variant="body2" color="secondary" fontWeight="regular">
                        Il n&apos;y a pas de colis calculé
                      </MDTypography>
                    </MDBox>
                  )}
                </Card>
              </Grid>
              <Grid item xs={12} md={5}>
                <Card id="calculate-package-revenue">
                  <MDBox component="form" onSubmit={handleSubmit(calculateTotalRevenue)} noValidate autoComplete="off">
                    <MDBox pt={2} px={2}>
                      <Controller
                        control={control}
                        name="barcodes"
                        defaultValue=""
                        rules={{
                          required: {
                            value: true,
                            message: "Entrez un ou une liste de codes-barres séparés par une virgule",
                          },
                        }}
                        render={({ field: { ref, ...field }, fieldState: { error } }) => (
                          <MDInput
                            fullWidth
                            variant="outlined"
                            type="text"
                            id="id-barcodes"
                            label="Codes-barres"
                            margin="normal"
                            placeholder="Ex: 123456789, 987654321"
                            multiline
                            rows={5}
                            error={Boolean(error)}
                            helperText={error && error.message}
                            {...field}
                          />
                        )}
                      />
                    </MDBox>
                    {isLoading ? (
                      <MDBox py={4} px={2} display="flex" justifyContent="center" alignItems="center">
                        <CircularProgress color="secondary" />
                      </MDBox>
                    ) : (
                      <>
                        <MDBox pt={2} px={2} display="flex" justifyContent="space-between" alignItems="center">
                          <MDTypography variant="h6" fontWeight="medium">
                            Calculer le revenu total
                          </MDTypography>
                          <MDButton type="submit" variant="gradient" color="dark">
                            <Icon>assessment</Icon>
                            &nbsp;Calculer
                          </MDButton>
                        </MDBox>
                        <MDBox p={2}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={12}>
                              <MDTypography variant="body1" fontWeight="medium" color="info">
                                {totalRevenue.toFixed(3)} TND
                              </MDTypography>
                              {Boolean(foreignTotalRevenue) &&
                                (diffPrice(totalRevenue, foreignTotalRevenue) !== 0 ? (
                                  <MDTypography variant="body2" fontWeight="medium" color="secondary">
                                    Le prix étranger a été modifié&nbsp;
                                    <span
                                      style={{
                                        color: "#fb8c00",
                                      }}
                                    >
                                      {diffPrice(totalRevenue, foreignTotalRevenue).toFixed(3)} TND
                                    </span>
                                  </MDTypography>
                                ) : (
                                  <MDTypography variant="body2" fontWeight="medium" color="secondary">
                                    Le prix étranger convient&nbsp;
                                    <span
                                      style={{
                                        color: "#4CAF50",
                                      }}
                                    >
                                      {diffPrice(totalRevenue, foreignTotalRevenue).toFixed(3)} TND
                                    </span>
                                  </MDTypography>
                                ))}
                            </Grid>
                          </Grid>
                        </MDBox>
                      </>
                    )}
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      </DashboardLayout>
      <MDSnackbar
        color={snackbarObject.color}
        icon={snackbarObject.icon}
        title={snackbarObject.title}
        content={snackbarObject.content}
        open={snackbarObject.open}
        close={handleCloseSnackbar}
        dateTime=""
      />
    </>
  )
}

export default CalculateRevenue
